import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import FormButton from 'components/form-components/FormButton';
import AlertModal from 'components/common-components/AlertModal';
import TabLayout from 'components/tab-components/TabLayout';
import TabHeader from 'components/tab-components/TabHeader';
import TabBody from 'components/tab-components/TabBody';
import {
  WIDGET_DEFAULT_SIZE,
  buildDimensionsPayload,
  findAvailablePosition,
  getDefaultValues,
  mapApiDimensionsToUi,
  newWidgetTabList,
  staticDimensionsOptions,
} from '../utils';
import { CallAPI } from 'actions/General';
import toast from 'react-hot-toast';
import BasicTab from './BasicTab';
import AdvanceTab from './AdvanceTab';
import { cloneDeep } from 'lodash';
import { uploadFile } from 'pages/company/utils';

const DashboardAddWidgetModal = ({
  editMode,
  widgetData,
  handleClose,
  pageId,
  dashboardId,
  widgetList,
  currentWidgetsRows,
  getPageDetails,
  setSaveLoader,
}) => {
  const { t } = useTranslation();

  const initialValues = useMemo(() => cloneDeep(getDefaultValues()), []);

  const dimensionsLookupObject = useRef({});
  const pageLookup = useRef({}).current;
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [selectedTab, setSelectedTab] = useState(newWidgetTabList[0]);
  const [dimensionsOptions, setDimensionsOptions] = useState({});
  const [staticDimensionsTypes, setStaticDimensionsTypes] = useState({});
  const [dashboardOptions, setDashboardOptions] = useState({});

  const [imageWidget, setImageWidget] = useState({
    oldImageUrl: null,
    newImage: null,
  });

  const handleReset = () => {
    setValues(cloneDeep(getDefaultValues(values.basicProps.chartType)));
  };

  const handleChange = (scope, field, value, path, index) => {
    setValues((prev) => {
      const nextState = cloneDeep(prev);

      if (scope === 'basicProps') {
        if (field === 'chartType') {
          const fresh = cloneDeep(getDefaultValues(value));
          fresh.basicProps.chartType = value;
          return fresh;
        }

        if (field === 'calculation') {
          nextState.basicProps.calculation = value;
          nextState.basicProps.label = buildLabel(value, prev.basicProps.dimensions);
          return nextState;
        }

        if (field.startsWith('dimensions')) {
          const updatedDims = [...prev.basicProps.dimensions];
          updatedDims[index] = value;

          nextState.basicProps.dimensions = updatedDims;
          nextState.basicProps.label = buildLabel(prev.basicProps.calculation, updatedDims);
          return nextState;
        }

        nextState.basicProps[field] = value;
        return nextState;
      } else if (scope === 'advancedProps') {
        if (field === 'formatting' && value === 'default') {
          nextState.advancedProps.formatting = 'default';
          nextState.advancedProps.formattingRanges = [];
          return nextState;
        }

        nextState.advancedProps = {
          ...prev.advancedProps,
          [field]: value,
        };
        return nextState;
      }

      nextState[field] = value;
      return nextState;
    });

    setTouched((t) => ({ ...t, [`${scope}.${field}`]: true }));
    setErrors((e) => ({ ...e, [`${scope}.${field}`]: undefined }));
  };

  const buildLabel = (calculation, dimensions = []) => {
    const dimensionLabel = dimensions
      .map((d) => dimensionsLookupObject.current[d[0]]?.type)
      .filter(Boolean)
      .join(' - ');
    return dimensionLabel ? `${calculation} - ${dimensionLabel}` : calculation || '';
  };

  const isValid = () => {
    const { basicProps, advancedProps } = values;

    if ('calculation' in basicProps && !basicProps.calculation) {
      toast.error(t('text_please_select_calculation'));
      return false;
    }

    if ('dimensions' in basicProps) {
      const areAllDimensionsFilled = basicProps.dimensions.every((item) => item.length > 0);

      if (!areAllDimensionsFilled) {
        toast.error(t('text_please_select_dimensions'));
        return false;
      }

      // make sure every dimension has different type
      const seenTypes = {};
      const hasDuplicateType =
        basicProps.dimensions.length > 1 &&
        basicProps.dimensions.some((dim) => {
          const type = dimensionsLookupObject.current[dim[0]]?.type;
          if (!type) return false;
          if (seenTypes[type]) {
            return true;
          }
          seenTypes[type] = true;
          return false;
        });
      if (hasDuplicateType) {
        toast.error(t('text_duplicate_dimension_type_found'));
        return false;
      }
    }

    if ('textLinks' in basicProps) {
      if (basicProps.textLinks.length === 0 || !basicProps.textLinks.every((item) => !!item.text)) {
        toast.error(t('text_text_and_link_field_error'));
        return false;
      }
    }

    if ('widgetImage' in basicProps && !(imageWidget.oldImageUrl || imageWidget.newImage)) {
      toast.error(t('text_image_is_required'));
      return false;
    }

    if ('formatting' in advancedProps && advancedProps.formatting === 'conditional') {
      if (
        advancedProps.formattingRanges.length === 0 ||
        !advancedProps.formattingRanges.every((item) => item.from && item.to && item.color)
      ) {
        toast.error(t('text_value_ranges_field_error'));
        return false;
      }
    }

    return true;
  };

  const buildPayload = async () => {
    let availablePosition = {};
    if (!editMode) {
      availablePosition = findAvailablePosition(
        widgetList,
        WIDGET_DEFAULT_SIZE[values.basicProps.chartType],
        currentWidgetsRows + WIDGET_DEFAULT_SIZE[values.basicProps.chartType].takeRows,
      );
      if (!availablePosition) return null;
    }

    const payload = {
      ...(editMode ? {} : { pageId: pageId }),
      ...(editMode ? {} : availablePosition),
      basicProps: cloneDeep(values.basicProps),
      advancedProps: {
        formattingRanges: [],
        ...cloneDeep(values.advancedProps),
      },
    };

    if (payload.basicProps.dimensions) {
      payload.basicProps.dimensions = buildDimensionsPayload(
        payload.basicProps.dimensions,
        dimensionsLookupObject.current,
        staticDimensionsTypes,
      );
    }

    if (payload.basicProps.widgetImage && imageWidget.newImage) {
      // delete old image first
      if (imageWidget.oldImageUrl) {
        await CallAPI('DELETE_MEDIA', {
          folderType: 'internal-dashboard',
          mediaId: payload.basicProps.widgetImage.mediaId,
          mediaType: 'image',
          imageName: payload.basicProps.widgetImage.imageName,
          dashboardId: dashboardId,
          dashboardPageId: pageId,
        });
      }

      const uploadedImage = await handleImageUpload(imageWidget.newImage);
      if (!uploadedImage) {
        toast.error(t('text_image_upload_failed_error'));
        return false;
      }
      payload.basicProps.widgetImage = {
        mediaId: uploadedImage.id,
        mediaType: 'image',
        imageName: uploadedImage.updatedImageName,
        dashboardId,
        dashboardPageId: pageId,
      };
    }
    return payload;
  };

  const handleSave = async () => {
    if (!isValid()) return;
    setSaveLoader(true);
    const payload = await buildPayload();
    if (payload) {
      const apiType = editMode ? 'EDIT_DASHBOARD_WIDGET' : 'ADD_DASHBOARD_WIDGET';
      const result = await CallAPI(apiType, payload, widgetData?._id);
      if (result.status) {
        toast.success(
          t(
            editMode
              ? 'alert_success_dashboard_widget_updated'
              : 'alert_success_dashboard_widget_added',
          ),
        );
        getPageDetails();
        handleClose();
      }
    }
    setSaveLoader(false);
  };

  const handleImageUpload = async (file) => {
    const fileMetaData = {
      imageName: `${Date.now()}${file.name.slice(file.name.lastIndexOf('.'))}`,
      folderType: 'internal-dashboard',
      mediaType: 'image',
      dashboardId: dashboardId,
      dashboardPageId: pageId,
    };
    const response = await CallAPI('GET_SINGLE_MEDIA_UPLOAD_URL', {
      ...fileMetaData,
      mediaType: 'image',
    });
    if (response.status) {
      await uploadFile(file, response.data);
      return { ...response.data };
    }
    return null;
  };

  const fetchDimensionsOptions = async () => {
    const result = await CallAPI('GET_DASHBOARD_WIDGET_DIMENSIONS_OPTIONS', {});

    const companyFieldsOptions = [];
    const formSetsOptions = [];
    const questionSetsOptions = [];
    const formsTitlesOptions = [];

    if (result.status) {
      result.data.companyFieldsList.forEach((field) => {
        companyFieldsOptions.push({
          value: field._id,
          displayValue: field.fieldTitle,
        });
        dimensionsLookupObject.current[field._id] = {
          type: 'companyFields',
          displayValue: field.fieldTitle,
          dbColumn: field.dbColumn,
          key: field.dbColumn,
        };
      });

      result.data.formSets.forEach((set) => {
        formSetsOptions.push({
          value: set._id,
          displayValue: set.title,
        });
        dimensionsLookupObject.current[set._id] = {
          type: 'formSets',
          displayValue: set.title,
          sqlColumn: set.sqlColumn,
          key: set.sqlColumn,
        };
      });

      result.data.questionSets.forEach((set) => {
        questionSetsOptions.push({
          value: set._id,
          displayValue: set.title,
        });
        dimensionsLookupObject.current[set._id] = {
          type: 'questionSets',
          displayValue: set.title,
          sqlColumn: set.sqlColumn,
          key: set.sqlColumn,
        };
      });

      result.data.formsList.forEach((form) => {
        formsTitlesOptions.push({
          value: form._id,
          displayValue: form.title,
        });
        dimensionsLookupObject.current[form._id] = {
          type: 'formTitles',
          displayValue: form.title,
          key: null,
        };
      });

      const allOptions = {
        children: [
          {
            value: 'companyFields',
            displayValue: 'select_company_fields',
            selectMultiple: true,
            children: companyFieldsOptions,
          },
          {
            value: 'formSets',
            displayValue: 'select_froms_sets',
            selectMultiple: true,
            children: formSetsOptions,
          },
          {
            value: 'questionSets',
            displayValue: 'select_questions_sets',
            selectMultiple: true,
            children: questionSetsOptions,
          },
          {
            value: 'formTitles',
            displayValue: 'select_from_titles',
            selectMultiple: true,
            children: formsTitlesOptions,
          },
          ...staticDimensionsOptions,
        ],
      };
      setDimensionsOptions(allOptions);
    }
  };

  const fetchDashboardList = async () => {
    const result = await CallAPI('GET_DASHBOARDS_LIST', {});
    if (result.status) {
      const finalObject = {
        children: [
          { value: dashboardId, displayValue: 'select_to_this_dashboard', children: [] },
          { value: 'to-other-dashboard', displayValue: 'select_to_other_dashboard', children: [] },
        ],
      };

      for (const dashboard of result.data) {
        const pages = dashboard.pages.map((page) => {
          pageLookup[page._id] = {
            dashboardName: dashboard.dashboardTitle,
            pageName: page.pageTitle,
          };
          const pageObj = {
            value: page._id,
            displayValue: page.pageTitle,
          };
          if (dashboard._id === dashboardId && page._id === pageId) {
            pageObj.disabled = true; // disable current page
          }
          return pageObj;
        });

        if (dashboard._id === dashboardId) {
          // Add pages under "To this dashboard"
          finalObject.children[0].children.push(...pages);
        } else {
          // Add pages under "to other dashboards"
          finalObject.children[1].children.push({
            value: dashboard._id,
            displayValue: dashboard.dashboardTitle,
            children: pages,
          });
        }
      }
      setDashboardOptions(finalObject);
    }
  };

  useEffect(() => {
    fetchDimensionsOptions();
    fetchDashboardList();
  }, []);

  useEffect(() => {
    const types = [];
    staticDimensionsOptions.forEach((option) => {
      types.push(option.value);
      dimensionsLookupObject.current[option.value] = {
        type: option.value,
        displayValue: option.displayValue,
        key: null,
      };
    });
    setStaticDimensionsTypes(types);
  }, []);

  const getExistingImageUrl = async (value) => {
    const mediaPayload = [
      {
        folderType: 'internal-dashboard',
        mediaId: value.mediaId,
        mediaType: value.mediaType,
        imageName: value.imageName,
        dashboardId: dashboardId,
        dashboardPageId: pageId,
      },
    ];

    const response = await CallAPI('GET_GENERIC_BULK_DOWNLOAD_MEDIA_REDIRECT_URL', {
      medias: mediaPayload,
    });
    if (response.status) {
      const imageUrl = response.data[0].url;

      if (imageUrl) {
        setImageWidget((prevState) => ({ ...prevState, oldImageUrl: imageUrl }));
      }
    }
  };

  const filterPropsByDefaults = (defaults, incoming) => {
    const filtered = {};
    Object.keys(defaults).forEach((key) => {
      if (key in incoming) {
        filtered[key] = incoming[key];
      }
    });
    return filtered;
  };

  useEffect(() => {
    if (!editMode) return;

    const { basicProps, advancedProps } = cloneDeep(widgetData);

    const defaults = getDefaultValues(basicProps.chartType);

    if (basicProps.dimensions) {
      basicProps.dimensions = mapApiDimensionsToUi(basicProps.dimensions);
    }

    const filteredBasicProps = filterPropsByDefaults(defaults.basicProps, basicProps);
    const filteredAdvancedProps = filterPropsByDefaults(
      defaults.advancedProps,
      advancedProps || {},
    );

    setValues({
      basicProps: {
        ...defaults.basicProps,
        ...filteredBasicProps,
      },
      advancedProps: {
        ...defaults.advancedProps,
        ...filteredAdvancedProps,
      },
    });

    if ('widgetImage' in defaults.basicProps) {
      getExistingImageUrl(basicProps.widgetImage);
    }
  }, [editMode]);

  return (
    <div className=''>
      <AlertModal show={true} actionButtons={[]} noPadding classes='dashboard-add-widget-modal'>
        <div className='w-100 theme-font-inter-regular'>
          <div className='d-flex px-4 py-3 border-bottom-grey-10-h-1'>
            <label className='theme-size-1_5 theme-font-inter-medium theme-text-black-1'>
              {t(editMode ? 'text_edit_widget' : 'text_new_widget')}
            </label>
          </div>

          <TabLayout>
            <div className='px-3 d-flex justify-content-between align-items-center'>
              <div className='w-50'>
                <TabHeader
                  tabList={newWidgetTabList}
                  selectedTab={selectedTab}
                  setSelectedTab={setSelectedTab}
                />
              </div>
              <div className='cursor-pointer p-2' onClick={handleReset}>
                {t('button_reset')}
              </div>
            </div>
            <TabBody>
              <div className='theme-background-white-5 px-4 py-3'>
                {selectedTab.value === 'advance' ? (
                  <AdvanceTab
                    values={values.advancedProps}
                    errors={errors}
                    touched={touched}
                    dashboardOptions={dashboardOptions}
                    pageLookup={pageLookup}
                    handleChange={(field, value, path, index) => {
                      handleChange('advancedProps', field, value, path, index);
                    }}
                  />
                ) : (
                  <BasicTab
                    values={values.basicProps}
                    errors={errors}
                    touched={touched}
                    handleChange={(field, value, path, index) => {
                      handleChange('basicProps', field, value, path, index);
                    }}
                    dimensionsOptions={dimensionsOptions}
                    dimensionsLookup={dimensionsLookupObject.current}
                    dashboardOptions={dashboardOptions}
                    imageWidget={imageWidget}
                    setImageWidget={setImageWidget}
                    editMode={editMode}
                  />
                )}
              </div>

              <div className='d-flex gap-2 justify-content-end p-3 border-top-grey-10-h-1'>
                <FormButton text={'button_cancel'} variant='white-1' onClick={handleClose} />
                <FormButton
                  text={'button_save'}
                  variant='green-1'
                  onClick={handleSave}
                  disabled={!values.basicProps.chartType}
                />
              </div>
            </TabBody>
          </TabLayout>
        </div>
      </AlertModal>
    </div>
  );
};

DashboardAddWidgetModal.propTypes = {
  editMode: PropTypes.bool.isRequired,
  widgetData: PropTypes.object,
  handleClose: PropTypes.func.isRequired,
  pageId: PropTypes.string.isRequired,
  dashboardId: PropTypes.string.isRequired,
  widgetList: PropTypes.array.isRequired,
  currentWidgetsRows: PropTypes.number.isRequired,
  getPageDetails: PropTypes.func.isRequired,
  setSaveLoader: PropTypes.func.isRequired,
};

export default DashboardAddWidgetModal;
